import api from "./config";
import { getPresignedUrlHistoria, putFileToS3, confirmUploadHistoria, getPresignedUrlConsulta, confirmUploadConsulta } from "./labsS3"; 


// 📌 OBTENER DIAGNÓSTICOS (CATÁLOGO CIE) CON BÚSQUEDA
export const getCatalogoDiagnosticos = async (search, pacienteId, relacionTemporal = 0, fechaConsulta = null, numeroDiagnostico = 1) => {
  try {
    const fechaParam = fechaConsulta ? `&fecha_consulta=${fechaConsulta}` : "";
   const response = await api.get(
      `/catalogos/diagnosticos/?search=${search}&paciente_id=${pacienteId}&relacion_temporal=${relacionTemporal}${fechaParam}&numero_diagnostico=${numeroDiagnostico}`
    );

    return response.data;
  } catch (error) {
    console.error("Error obteniendo catálogo de diagnósticos:", error);
    return [];
  }
};


// 📌 OBTENER NOMBRES DE DIAGNÓSTICOS CIE POR CLAVE (solo para mostrar, sin filtros)
// Recibe ["J00X", "E119"] y regresa { J00X: "RINOFARINGITIS...", E119: "..." }
export const getNombresDiagnosticos = async (claves = []) => {
  const limpias = [
    ...new Set(
      claves
        .filter(Boolean)
        .map((c) => String(c).trim().toUpperCase())
    ),
  ];

  if (limpias.length === 0) return {};

  try {
    const response = await api.get(
      `/catalogos/diagnosticos/nombres/?claves=${encodeURIComponent(limpias.join(","))}`
    );
    return response.data || {};
  } catch (error) {
    console.error("Error obteniendo nombres de diagnósticos:", error);
    return {};
  }
};

// 📌 OBTENER HISTORIA CLÍNICA POR ID DEL PACIENTE
export const getHistoriaClinica = async (pacienteId) => {
    try {
        const response = await api.get(`/historias-clinicas/por_paciente/?paciente_id=${pacienteId}`); // ✅ Probable solución
        return response.data;
    } catch (error) {
        console.error(`Error al obtener historia clínica del paciente ${pacienteId}:`, error);
        return null;
    }
};

// 📌 OBTENER HISTORIA CLÍNICA POR ID DEL PACIENTE (ENDPOINT ESPECÍFICO)
export const getHistoriaClinicaPorPaciente = async (pacienteId) => {
    try {
        const response = await api.get(`/historias-clinicas/por_paciente/?paciente_id=${pacienteId}`);
        return response.data;
    } catch (error) {
        console.error(`Error al obtener historia clínica del paciente ${pacienteId}:`, error);
        return null;
    }
};

// 📌 DASHBOARD HISTORIAS (PACIENTE + HISTORIA + CONSULTAS)
export const getDashboardHistorias = async () => {
    try {
        const response = await api.get(`/historias-clinicas/dashboard/`);
        return response.data;
    } catch (error) {
        console.error("Error obteniendo dashboard de historias clínicas:", error);
        return [];
    }
};

// 📌 CREAR HISTORIA CLÍNICA
export const createHistoriaClinica = async (formData, uploadedFiles = []) => {
    try {
        // 📌 Verificar que `paciente_id` está presente
        if (!formData.get("paciente_id")) {
            console.error("❌ Error: paciente_id no está definido.");
            throw new Error("El campo paciente_id es obligatorio.");
        }
      

        // 📌 Hacer la petición POST
        const response = await api.post(`/historias-clinicas/`, formData, {
            headers: { "Content-Type": "multipart/form-data" },
        });
        
        const historiaId = response.data.id;  // YA EXISTE LA HISTORIA ANTES DE SUBIR ARCHIVOS

        // ============================================================
        // 🟢 2. AHORA SÍ SUBIR LOS LABS A S3 USANDO historiaId
        // ============================================================
        let labsS3 = [];

        if (uploadedFiles.length > 0) {
            for (const file of uploadedFiles) {
                
                // 2.1 Presigned (ya con historiaId)
                const { key, upload_url } = await getPresignedUrlHistoria(
                    historiaId,
                    file
                );
                
                // 2.2 PUT a S3
                await putFileToS3(upload_url, file);

                // 2.3 Confirmación en backend (ya con historiaId)
                const confirm = await confirmUploadHistoria(
                    historiaId,
                    key,
                    file
                );

                labsS3.push(confirm.lab);
            }
        }

        // ============================================================
        // 🟢 3. (Opcional) PATCH si backend requiere metadata completa
        // ============================================================
        if (labsS3.length > 0) {
            await api.patch(`/historias-clinicas/${historiaId}/`, {
                laboratorios_s3: labsS3,
            });
        }

        // ============================================================
        // 🟢 4. REGRESAR LA HISTORIA CREADA (MISMO COMPORTAMIENTO VIEJO)
        // ============================================================

      
        return response.data;
    } catch (error) {
        console.error("❌ Error creando historia clínica:", error);
        throw error;
    }
};


// 📌 ACTUALIZAR HISTORIA CLÍNICA
// 📌 ACTUALIZAR HISTORIA CLÍNICA
export const updateHistoriaClinica = async (id, historiaClinica) => {
    try {
        // Verificar datos antes de enviar  
        // Eliminar campo laboratorios si existe
        if (historiaClinica.laboratorios !== undefined) {
            delete historiaClinica.laboratorios;
        
        }
        
        // Si es un objeto pero no FormData, hacer una petición regular
        if (!(historiaClinica instanceof FormData)) {
           
            
            const response = await api.patch(`/historias-clinicas/${id}/`, historiaClinica);
           
            return response.data;
        }
        
        // Si es FormData, usar Content-Type multipart/form-data
      
        const response = await api.patch(`/historias-clinicas/${id}/`, historiaClinica, {
            headers: { "Content-Type": "multipart/form-data" },
        });
        
      
        return response.data;
    } catch (error) {
        console.error("❌ Error actualizando historia clínica:", error);
        if (error.response) {
            console.error("📌 Respuesta del servidor:", error.response.data);
        }
        throw error; // Lanza el error para manejarlo en el componente
    }
};

// 📌 AGREGAR CONSULTA A UNA HISTORIA CLÍNICA
export const addConsultaToHistoriaClinica = async (
  historiaId,
  nuevaConsulta,
  consultaFiles = []
) => {
  try {
    let response = null;

    // 🟦 1. POST ORIGINAL (NO SE TOCA)
    if (nuevaConsulta instanceof FormData) {
      response = await api.post(
        `/historias-clinicas/${historiaId}/add_consulta/`,
        nuevaConsulta,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
    } else {
      throw new Error("Consulta enviada sin FormData (flujo inválido)");
    }

    // 🟢 2. EL MISMO OBJETO QUE ANTES REGRESABAS
    const consultaCreada = response.data;
    const consultaId = consultaCreada.id;

    // 🟢 3. SUBIR ARCHIVOS A S3 COMO HISTORIA (CONSISTENTE)
    if (consultaFiles.length > 0) {
      for (const file of consultaFiles) {
        // 👉 3.1 obtener presigned URL
        const { key, upload_url } = await getPresignedUrlConsulta(
          consultaId,
          file
        );

        // 👉 3.2 subir archivo a S3
        await putFileToS3(upload_url, file);

        // 👉 3.3 confirmar en backend
        await confirmUploadConsulta(consultaId, key, file);
      }
    }

    // 🟢 4. RETURN EXACTAMENTE COMO ANTES
    return consultaCreada;

  } catch (error) {
    console.error("❌ Error agregando consulta a la historia clínica:", error);
    throw error;
  }
};

  

// 📌 ACTUALIZAR UNA CONSULTA
export const updateConsulta = async (consultaId, consultaActualizada) => {
    try {
        // Verificar si ya es un FormData
        let formData;
        if (consultaActualizada instanceof FormData) {
            formData = consultaActualizada;
        } else {
            formData = new FormData();
            
            Object.keys(consultaActualizada).forEach(key => {
                if (key === 'upload_laboratorios' && Array.isArray(consultaActualizada[key])) {
                    consultaActualizada[key].forEach(file => {
                        formData.append('upload_laboratorios', file);
                    });
                } else if (typeof consultaActualizada[key] === 'object' && consultaActualizada[key] !== null && !(consultaActualizada[key] instanceof File)) {
                    formData.append(key, JSON.stringify(consultaActualizada[key]));
                } else {
                    formData.append(key, consultaActualizada[key]);
                }
            });
        }

        const response = await api.patch(`/consultas/${consultaId}/`, formData, {
            headers: { "Content-Type": "multipart/form-data" },
        });
        return response.data;
    } catch (error) {
        console.error("❌ Error actualizando consulta en la historia clínica:", error);
        throw error;
    }
};

// 📌 SUBIR ARCHIVO DE LABORATORIO
export const uploadLaboratorio = async (historiaId, files, descripcion = "") => {
    try {
        const formData = new FormData();
        
        // Verificar si es un solo archivo o un array
        if (Array.isArray(files)) {
            files.forEach(file => {
                formData.append("archivo", file);
            });
        } else {
            formData.append("archivo", files);
        }
        
        // Añadir descripción si existe
        if (descripcion) {
            formData.append("descripcion", descripcion);
        }

        const response = await api.post(`/historias-clinicas/${historiaId}/upload_laboratorio/`, formData, {
            headers: { "Content-Type": "multipart/form-data" }
        });

        return response.data;
    } catch (error) {
        console.error("Error subiendo laboratorio:", error);
        throw error;
    }
};

// 📌 OBTENER CONSULTAS DE UNA HISTORIA CLÍNICA
// 📌 OBTENER TODAS LAS CONSULTAS DE UNA HISTORIA CLÍNICA
export const getConsultasHistoriaClinica = async (historiaId) => {
    try {
        const response = await api.get(`/consultas/?historia_clinica=${historiaId}`);
        return response.data;
    } catch (error) {
        console.error(`Error al obtener consultas de la historia clínica ${historiaId}:`, error);
        return [];
    }
};


export const auditarExpedienteExportado = async (pacienteId, consultaId = null) => {
  try {
    await api.post("/auditoria/expediente/exportado/", {
      paciente_id: pacienteId,
      consulta_id: consultaId,
    });
  } catch (error) {
    console.error("Error auditando exportación de expediente:", error);
  }
};

export const auditarHojaDiariaExportada = async ({
  fechaInicio,
  fechaFin,
  totalRegistros,
}) => {
  try {
    await api.post("/auditoria/hoja-diaria/exportada/", {
      fecha_inicio: fechaInicio,
      fecha_fin: fechaFin,
      total_registros: totalRegistros,
      tipo_export: "pdf",
    });
  } catch (error) {
    console.error("Error auditando exportación de hoja diaria:", error);
  }
};

export const auditarArchivoIntercambioExportado = async ({
  fechaInicio,
  fechaFin,
  formato,
}) => {
  try {
    await api.post("/auditoria/archivo-intercambio/exportado/", {
      fecha_inicio: fechaInicio,
      fecha_fin: fechaFin,
      tipo_export: formato,
    });
  } catch (error) {
    console.error(
      "Error auditando exportación de archivo de intercambio:",
      error
    );
  }
};


// ACATALOGO SERVICIOS ATENCION POR PERSONAL
export const getCatalogoServiciosAtencion = async () => {
    try {
        const response = await api.get(`/catalogos/servicios_atencion_por_tipo_personal/`);
        return response.data;
    } catch (error) {
        console.error("Error obteniendo catálogo de servicios de atención:", error);
        return [];
    }
};

export const getHojaDiaria = async (fechaInicio, fechaFin) => {
    try {
        const response = await api.get(
            `/historias-clinicas/hoja-diaria/?fecha_inicio=${fechaInicio}&fecha_fin=${fechaFin}`
        );
        return response.data;
    } catch (error) {
        console.error("Error obteniendo hoja diaria:", error);
        throw error;
    }
};
export const descargarArchivoIntercambio = async (
  fechaInicio,
  fechaFin,
  formato
) => {
  const response = await api.get(
    `/historias-clinicas/exportar-nom/?fecha_inicio=${fechaInicio}&fecha_fin=${fechaFin}&formato=${formato}`,
    {
      responseType: "blob",
    }
  );

  return response;
};

// 📌 OBTENER SUGERENCIAS HISTÓRICAS DEL EXPEDIENTE
export const getSugerenciasHistoricasExpediente = async (campo, search) => {
  try {
    const response = await api.get(
      `/historias-clinicas/sugerencias-historicas/?campo=${encodeURIComponent(campo)}&q=${encodeURIComponent(search)}`
    );

    return Array.isArray(response.data) ? response.data : [];
  } catch (error) {
    console.error("Error obteniendo sugerencias históricas:", error);
    return [];
  }
};